import { useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useTourGuide, TourGuideConfig } from "./useTourGuide";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { updateTourCompletion } from "@store/thunks/userProfile";
import { ROUTE_NAMES } from "@root/constants";
import { TourGuideType } from "@root/constants/tour-guide";
import useMediaQ from "@root/hooks/useMediaQ";

/**
 * Tour Guide Hook for Journalist Customer Center
 *
 * Steps: FAQ (expand to see answer), Inquiries tab, Submit button (response on email), Resources tab (finish).
 */
export const useJournalistCustomerCenterTourGuide = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { setActiveTourModule, setCurrentTourStep } = useTourGuideContext();
  const { sm } = useMediaQ();
  const isMobile = !sm;

  const isOnCustomerCenter =
    pathname === ROUTE_NAMES.JOURNALIST_CUSTOMER_CENTER;

  const config: TourGuideConfig = useMemo(
    () => ({
      moduleId: "journalistCustomerCenter",
      enabled: isOnCustomerCenter,
      steps: [
        {
          id: "journalist-customer-center-faq",
          targetSelector: '[data-tour-item="journalist-customer-center-faq"]',
          title: t("tourGuide.journalistCustomerCenter.step1.title"),
          description: t(
            "tourGuide.journalistCustomerCenter.step1.description",
          ),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
        {
          id: "journalist-customer-center-inquiries-tab",
          targetSelector:
            '[data-tour-item="journalist-customer-center-inquiries-tab"]',
          title: t("tourGuide.journalistCustomerCenter.step2.title"),
          description: t(
            "tourGuide.journalistCustomerCenter.step2.description",
          ),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
        {
          id: "journalist-customer-center-inquiry-submit",
          targetSelector:
            '[data-tour-item="journalist-customer-center-inquiry-submit"]',
          title: t("tourGuide.journalistCustomerCenter.step3.title"),
          description: t(
            "tourGuide.journalistCustomerCenter.step3.description",
          ),
          position: "top",
          offset: { x: 0, y: -15 },
          highlightPadding: 10,
        },
        {
          id: "journalist-customer-center-resources-tab",
          targetSelector:
            '[data-tour-item="journalist-customer-center-resources-tab"]',
          title: t("tourGuide.journalistCustomerCenter.step4.title"),
          description: t(
            "tourGuide.journalistCustomerCenter.step4.description",
          ),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
      ],
    }),
    [isOnCustomerCenter, t],
  );

  const baseTourGuide = useTourGuide(config);

  const handleTourComplete = () => {
    dispatch(
      updateTourCompletion({
        payload: {
          tourGuides: [
            { type: TourGuideType.JournalistCustomerCenter, isDone: true },
          ],
        },
        onSuccess: () => {},
        onError: () => {},
      }) as any,
    );
  };

  const journalistCustomerCenterTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourGuideType: TourGuideType.JournalistCustomerCenter,
    isOnTourPath: isOnCustomerCenter && !isMobile,
    onTourComplete: handleTourComplete,
  });

  useEffect(() => {
    if (journalistCustomerCenterTourGuide.isActive) {
      setActiveTourModule("journalistCustomerCenter");
      setCurrentTourStep(journalistCustomerCenterTourGuide.currentStep);
    } else {
      setActiveTourModule(null);
    }
  }, [
    journalistCustomerCenterTourGuide.isActive,
    journalistCustomerCenterTourGuide.currentStep,
    setActiveTourModule,
    setCurrentTourStep,
  ]);

  return {
    journalistCustomerCenterTourGuide,
    isMobile,
  };
};
